import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useLanguage } from '../../../contexts/LanguageContext';

const FooterSection = () => {
  const currentYear = new Date()?.getFullYear();
  const { t, language } = useLanguage();

  const footerLinks = {
    product: [
      { label: language === 'bn' ? 'বৈশিষ্ট্য' : 'Features', path: '/#features', isHash: true },
      { label: language === 'bn' ? 'কীভাবে কাজ করে' : 'How It Works', path: '/#how-it-works', isHash: true },
      { label: language === 'bn' ? 'সম্পত্তি খুঁজুন' : 'Search Properties', path: '/search-results', isHash: false },
      { label: language === 'bn' ? 'সাধারণ প্রশ্নাবলী' : 'FAQ', path: '/faq', isHash: false },
    ],
    company: [
      { label: language === 'bn' ? 'আমাদের সম্পর্কে' : 'About Us', path: '/about-us', isHash: false },
      { label: language === 'bn' ? 'যোগাযোগ' : 'Contact', path: '/contact-us', isHash: false },
      { label: language === 'bn' ? 'পেশাদার মার্কেটপ্লেস' : 'Professional Marketplace', path: '/professional-marketplace', isHash: false },
      { label: language === 'bn' ? 'পেশাদার হিসেবে যোগ দিন' : 'Join as Professional', path: '/join-as-professional', isHash: false },
    ],
    legal: [
      { label: language === 'bn' ? 'গোপনীয়তা নীতি' : 'Privacy Policy', path: '/privacy-policy', isHash: false },
      { label: language === 'bn' ? 'সেবার শর্তাবলী' : 'Terms of Service', path: '/terms-of-service', isHash: false },
      { label: language === 'bn' ? 'কুকি নীতি' : 'Cookie Policy', path: '/privacy-policy#cookies', isHash: false },
      { label: language === 'bn' ? 'দায়বদ্ধতা অস্বীকৃতি' : 'Disclaimer', path: '/terms-of-service#liability', isHash: false },
    ],
    support: [
      { label: language === 'bn' ? 'সহায়তা কেন্দ্র' : 'Help Center', path: '/help-support', isHash: false },
      { label: language === 'bn' ? 'সাধারণ প্রশ্নাবলী' : 'FAQ', path: '/faq', isHash: false },
      { label: language === 'bn' ? 'সাপোর্টে যোগাযোগ' : 'Contact Support', path: '/contact-us', isHash: false },
      { label: language === 'bn' ? 'সিস্টেম স্ট্যাটাস' : 'System Status', path: '/contact-us', isHash: false },
    ],
  };

  const socialLinks = [
    { icon: 'Facebook', url: 'https://facebook.com', label: 'Facebook' },
    { icon: 'Twitter', url: 'https://twitter.com', label: 'Twitter' },
    { icon: 'Linkedin', url: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: 'Instagram', url: 'https://instagram.com', label: 'Instagram' },
  ];

  const renderLink = (link) => {
    if (link?.isHash) {
      return (
        <a
          href={link?.path}
          className="text-sm md:text-base text-muted-foreground hover:text-primary transition-colors duration-200"
        >
          {link?.label}
        </a>
      );
    }
    return (
      <Link
        to={link?.path}
        className="text-sm md:text-base text-muted-foreground hover:text-primary transition-colors duration-200"
      >
        {link?.label}
      </Link>
    );
  };

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 md:gap-12 mb-12 md:mb-16">
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-4 md:mb-6">
              <img
                src="/assets/images/logo_LL-1772022913346.jpeg"
                alt="ListingLens logo"
                className="h-10 w-auto rounded-lg object-cover"
                style={{ boxShadow: '0 0 12px rgba(0, 212, 255, 0.4)', border: '1px solid rgba(0,212,255,0.3)', maxWidth: '140px' }}
              />
            </Link>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6">
              {t('footerTagline')}
            </p>
            <div className="flex items-center gap-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.label}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social?.label}
                  className="w-10 h-10 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all duration-300"
                >
                  <Icon name={social?.icon} size={20} color="currentColor" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-sm md:text-base font-semibold text-foreground mb-4 md:mb-6">{language === 'bn' ? 'পণ্য' : 'Product'}</h3>
            <ul className="space-y-3">
              {footerLinks?.product?.map((link) => (
                <li key={link?.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm md:text-base font-semibold text-foreground mb-4 md:mb-6">{language === 'bn' ? 'কোম্পানি' : 'Company'}</h3>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link) => (
                <li key={link?.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm md:text-base font-semibold text-foreground mb-4 md:mb-6">{language === 'bn' ? 'আইনি' : 'Legal'}</h3>
            <ul className="space-y-3">
              {footerLinks?.legal?.map((link) => (
                <li key={link?.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm md:text-base font-semibold text-foreground mb-4 md:mb-6">{language === 'bn' ? 'সহায়তা' : 'Support'}</h3>
            <ul className="space-y-3">
              {footerLinks?.support?.map((link) => (
                <li key={link?.label}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              &copy; {currentYear} ListingLens. {t('allRightsReserved')} {language === 'bn' ? '১০০% অস্ট্রেলিয়ান মার্কেট কভারেজ' : '100% Australian Market Coverage'} — NSW · VIC · QLD · WA · SA · ACT · TAS · NT
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy-policy" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                {language === 'bn' ? 'গোপনীয়তা' : 'Privacy'}
              </Link>
              <Link to="/terms-of-service" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                {language === 'bn' ? 'শর্তাবলী' : 'Terms'}
              </Link>
              <Link to="/privacy-policy#cookies" className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200">
                {language === 'bn' ? 'কুকি' : 'Cookies'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;